import React, { useState } from "react";
import {
  Popover,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@material-ui/core";

export const FilterPopover = ({
  open,
  anchorEl,
  onClose,
  field: _field,
  fieldType,
  onApply,
  onClear,
  currentFilter,
}) => {
  // Get default operator based on field type
  const getDefaultOperator = () => {
    if (fieldType === "date") {
      return "equals";
    } else if (fieldType === "number" || fieldType === "currency" || fieldType === "percentage" || 
               fieldType === "acv" || fieldType === "count") {
      return "equals";
    } else {
      return "contains";
    }
  };

  const [operator, setOperator] = useState(
    (currentFilter?.operator) || getDefaultOperator()
  );
  const [value, setValue] = useState(currentFilter?.value || "");
  // For date range, store start and end dates separately
  const [rangeStart, setRangeStart] = useState(
    currentFilter?.rangeStart || ""
  );
  const [rangeEnd, setRangeEnd] = useState(
    currentFilter?.rangeEnd || ""
  );

  // Convert date from YYYY-MM-DD to MM-DD-YYYY format
  const convertToMMDDYYYY = (dateString) => {
    if (!dateString) return "";
    // If already in mm-dd-yyyy format, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }
    // If in yyyy-mm-dd format, convert to mm-dd-yyyy
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split("-");
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return dateString;
  };

  // Convert date from MM-DD-YYYY to YYYY-MM-DD format (for internal processing)
  const convertToYYYYMMDD = (dateString) => {
    if (!dateString) return "";
    // If in mm-dd-yyyy format, convert to yyyy-mm-dd
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const parts = dateString.split("-");
      return `${parts[2]}-${parts[0]}-${parts[1]}`;
    }
    // If already in yyyy-mm-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    return dateString;
  };

  React.useEffect(() => {
    if (currentFilter) {
      setOperator(currentFilter.operator);
      
      // Parse range value for date inRange operator
      if (fieldType === "date" && currentFilter.operator === "inRange" && currentFilter.value) {
        const rangeParts = currentFilter.value.split("|");
        if (rangeParts.length === 2) {
          // Convert from yyyy-mm-dd to mm-dd-yyyy for display
          setRangeStart(convertToMMDDYYYY(rangeParts[0] || ""));
          setRangeEnd(convertToMMDDYYYY(rangeParts[1] || ""));
          setValue("");
        } else {
          setRangeStart(convertToMMDDYYYY(currentFilter.rangeStart || ""));
          setRangeEnd(convertToMMDDYYYY(currentFilter.rangeEnd || ""));
          setValue("");
        }
      } else {
        // Convert from yyyy-mm-dd to mm-dd-yyyy for display
        setValue(convertToMMDDYYYY(currentFilter.value || ""));
        setRangeStart("");
        setRangeEnd("");
      }
    } else {
      setOperator(getDefaultOperator());
      setValue("");
      setRangeStart("");
      setRangeEnd("");
    }
  }, [currentFilter, open, fieldType]);

  const handleApply = () => {
    // For date range, combine start and end dates
    if (fieldType === "date" && operator === "inRange") {
      if (rangeStart && rangeEnd) {
        // Convert from mm-dd-yyyy to yyyy-mm-dd for internal processing
        const startDate = convertToYYYYMMDD(rangeStart);
        const endDate = convertToYYYYMMDD(rangeEnd);
        // Pass range as a combined value: "startDate|endDate"
        onApply(operator, `${startDate}|${endDate}`);
      }
    } else if (value.trim() !== "" || operator === "notContains" || operator === "notEquals") {
      // For date fields, convert from mm-dd-yyyy to yyyy-mm-dd for internal processing
      const filterValue = fieldType === "date" ? convertToYYYYMMDD(value.trim()) : value.trim();
      onApply(operator, filterValue);
    }
    onClose();
  };

  const handleClear = () => {
    setOperator(getDefaultOperator());
    setValue("");
    setRangeStart("");
    setRangeEnd("");
    onClear();
    onClose();
  };

  // Get available operators based on field type
  const getAvailableOperators = () => {
    // String/Text operators
    if (fieldType === "text" || !fieldType) {
      return [
        { value: "contains", label: "Contains" },
        { value: "notContains", label: "Not Contains" },
        { value: "startsWith", label: "Starts with" },
        { value: "endsWith", label: "Ends with" },
      ];
    }
    
    // Number operators (number, currency, percentage, and original data types like acv, count)
    if (fieldType === "number" || fieldType === "currency" || fieldType === "percentage" || 
        fieldType === "acv" || fieldType === "count") {
      return [
        { value: "equals", label: "Equal" },
        { value: "notEquals", label: "Not equal" },
        { value: "lessThan", label: "Less than" },
        { value: "lessThanOrEqual", label: "Less than or equal" },
        { value: "greaterThan", label: "Greater than" },
        { value: "greaterThanOrEqual", label: "Greater than or equal" },
        { value: "inRange", label: "In Range" },
      ];
    }
    
    // Date operators
    if (fieldType === "date") {
      return [
        { value: "equals", label: "Equal" },
        { value: "notEquals", label: "Not equal" },
        { value: "lessThan", label: "Less than" },
        { value: "lessThanOrEqual", label: "Less than or equal" },
        { value: "greaterThan", label: "Greater than" },
        { value: "greaterThanOrEqual", label: "Greater than or equal" },
        { value: "inRange", label: "In Range" },
      ];
    }
    
    // Default fallback
    return [
      { value: "contains", label: "Contains" },
      { value: "notContains", label: "Not Contains" },
      { value: "startsWith", label: "Starts with" },
      { value: "endsWith", label: "Ends with" },
    ];
  };

  const operators = getAvailableOperators();

  // Clear range fields when operator changes away from inRange
  React.useEffect(() => {
    if (fieldType === "date" && operator !== "inRange") {
      setRangeStart("");
      setRangeEnd("");
    } else if (fieldType === "date" && operator === "inRange") {
      setValue("");
    }
  }, [operator, fieldType]);

  // Get placeholder text based on operator
  const getPlaceholder = () => {
    if (fieldType === "date" && operator === "inRange") {
      return "";
    }
    if (operator === "inRange") {
      return "Enter range (e.g., 10-20 or 10, 20)";
    }
    if (fieldType === "date") {
      return "mm-dd-yyyy";
    }
    return "Enter filter value";
  };

  // Get input type based on field type and operator
  const getInputType = () => {
    if (fieldType === "date") {
      return "text"; // Use text input to support mm-dd-yyyy format
    }
    // For "inRange" operator, use text input to allow range format
    if (operator === "inRange") {
      return "text";
    }
    if (fieldType === "number" || fieldType === "currency" || fieldType === "percentage" || 
        fieldType === "acv" || fieldType === "count") {
      return "number";
    }
    return "text";
  };

  // Validate and format date input to mm-dd-yyyy format
  const handleDateChange = (newValue, setter) => {
    if (!newValue) {
      setter("");
      return;
    }

    // Remove any non-digit and non-dash characters
    let cleaned = newValue.replace(/[^\d-]/g, "");
    
    // If user is typing, format as they type
    // Remove existing dashes and rebuild
    const digits = cleaned.replace(/-/g, "");
    
    if (digits.length === 0) {
      setter("");
      return;
    }
    
    // Format as mm-dd-yyyy
    let formatted = "";
    if (digits.length >= 1) {
      formatted = digits.substring(0, 2);
    }
    if (digits.length >= 3) {
      formatted += "-" + digits.substring(2, 4);
    }
    if (digits.length >= 5) {
      // Restrict year to 4 digits
      const year = digits.substring(4, 8);
      formatted += "-" + year;
    }
    
    // Only set if the formatted string is valid (complete or partial)
    // Allow partial dates while user is typing
    setter(formatted);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        style: {
          marginTop: 4,
          minWidth: 320,
          maxWidth: 400,
        },
      }}
    >
      <Box style={{ padding: 16 }}>
        <Box style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Operator</InputLabel>
            <Select
              value={operator}
              label="Operator"
              onChange={(e) => setOperator(e.target.value)}
            >
              {operators.map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {fieldType === "date" && operator === "inRange" ? (
            <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                value={rangeStart}
                onChange={(e) => handleDateChange(e.target.value, setRangeStart)}
                type="text"
                placeholder="mm-dd-yyyy"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="End Date"
                value={rangeEnd}
                onChange={(e) => handleDateChange(e.target.value, setRangeEnd)}
                type="text"
                placeholder="mm-dd-yyyy"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Value"
              value={value}
              onChange={(e) => {
                if (fieldType === "date") {
                  handleDateChange(e.target.value, setValue);
                } else {
                  setValue(e.target.value);
                }
              }}
              placeholder={getPlaceholder()}
              type={getInputType()}
              InputLabelProps={
                fieldType === "date"
                  ? {
                      shrink: true,
                    }
                  : {}
              }
            />
          )}
          <Box style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Button variant="outlined" size="small" onClick={handleClear}>
              Clear
            </Button>
            <Button variant="contained" size="small" onClick={handleApply}>
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Popover>
  );
};

